// "Marshy meadows of memories": a 4pm village angan → memory sounds → roots of lineage → melting into the painting.
export const marshyMeadows = {
  slug: "marshy-meadows-of-memories",
  duration: 29,
  music: "bansuri-folk",
  stages: [
    { id: "angan", at: 0, hint: "tap a pot to hear a memory" },
    { id: "memories", at: 8, hint: "tap a pot to hear a memory" },
    { id: "roots", at: 14, hint: "" },
    { id: "melt", at: 21, hint: "" },
  ],
  line: { text: "We are all born of villages that raised our souls; our memories keep us rooted.", from: 15, to: 27.5, pos: "low" },
  figures: {
    list: [
      { src: "/arcs/angan/pots.webp", th: 0, y: 0.95, w: 3.3, h: 2.2, tap: true },
      { src: "/arcs/angan/warli.webp", th: -1.05, y: 2.05, w: 3.4, h: 2.3 },
      { src: "/arcs/angan/madhubani.webp", th: 1.05, y: 2.2, w: 1.7, h: 1.7 },
      { src: "/arcs/angan/warli.webp", th: 2.45, y: 2.0, w: 3.0, h: 2.0 },
      { src: "/arcs/angan/neem.webp", th: -2.0, y: 4.9, w: 3.6, h: 5.4 },
      { src: "/arcs/angan/neem.webp", th: 1.85, y: 5.1, w: 3.9, h: 5.8 },
      { src: "/arcs/angan/neem.webp", th: 3.14, y: 4.7, w: 3.4, h: 5.0 },
    ],
    appear: [0, 1.5],
    thread: [99, 100],
    fade: [21.5, 25.5],
  },
  touch: { kind: "memory", until: 21 },
  agarbatti: { th: 0.52, fade: [21.5, 25] },
  ambient: { scene: "angan", until: 99 },
  look: { start: -0.2, lift: [12, 16], end: -0.05, yaw: [[0, 0], [29, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "angan", pos: "low" },
  speed: [[0, 0.2], [21, 0.2], [24, 0.06], [29, 0.04]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float ridge(vec2 p){ return 1.0 - abs(snoise(vec3(p, 2.7))); }
void main(){
  float roots = smoothstep(13.5, 20.5, uT);
  float melt = smoothstep(21.0, 26.0, uT);
  float n = snoise(vec3(vPos * 0.6)) * 0.5 + 0.5;
  float n2 = snoise(vec3(vPos * 2.3 + 5.0)) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  vec3 col;
  vec2 local;
  // low 4pm sun from behind the visitor's left: long warm shafts with streaks of dust
  float shaftC = th * uRadius * 0.55 + vPos.y * 0.9 + (uSurface > 0.5 ? length(vPos.xz) * 0.6 : 0.0);
  float shaft = smoothstep(0.35, 0.0, abs(fract(shaftC * 0.22 + 0.15) - 0.5) - 0.12) * (0.6 + 0.4 * n);
  float dust = smoothstep(0.985, 1.0, sin(shaftC * 60.0 + vPos.y * 3.0 + uT * 0.6 + n2 * 8.0)) * shaft * 0.6;
  if (uSurface < 0.5) {
    float y = vPos.y;
    vec3 mud = mix(vec3(0.66, 0.47, 0.32), vec3(0.76, 0.58, 0.4), n);
    mud = mix(mud, vec3(0.55, 0.38, 0.26), smoothstep(0.55, 0.8, n2) * 0.5);
    vec3 plinth = vec3(0.5, 0.36, 0.25);
    col = mix(plinth, mud, smoothstep(0.35, 0.45, y + n2 * 0.08));
    // the angan wall ends at a low parapet; above it the warm 4pm sky behind the trees
    float top = 3.35 + 0.06 * sin(th * 9.0);
    vec3 sky = mix(vec3(1.0, 0.86, 0.62), vec3(0.86, 0.8, 0.74), smoothstep(3.4, 9.0, y));
    col = mix(col, vec3(0.6, 0.43, 0.3), smoothstep(top - 0.18, top - 0.12, y) * 0.6);
    col = mix(col, sky, smoothstep(top - 0.02, top + 0.02, y));
    col = mix(col, vec3(0.32, 0.45, 0.2), (1.0 - smoothstep(0.0, 0.55, y + n * 0.35)) * 0.8);
    local = vec2(th * uRadius, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    float r = length(xz);
    vec3 earth = mix(vec3(0.5, 0.36, 0.24), vec3(0.6, 0.45, 0.3), n);
    float moss = smoothstep(0.55, 0.75, snoise(vec3(xz * 0.5, 9.0)) * 0.5 + 0.5 + smoothstep(uRadius - 2.5, uRadius, r) * 0.35);
    earth = mix(earth, mix(vec3(0.26, 0.4, 0.16), vec3(0.42, 0.55, 0.2), n2), moss * 0.85);
    // roots of lineage: veins growing out from the pots at the front edge
    vec2 o = vec2(0.0, -(uRadius - 0.6));
    float d = length(xz - o);
    vec2 w = xz + vec2(snoise(vec3(xz * 0.4, 1.0)), snoise(vec3(xz * 0.4, 4.0))) * 0.6;
    float v1 = pow(ridge(w * 0.55), 18.0);
    float v2 = pow(ridge(w * 1.3 + 7.0), 22.0) * 0.7;
    float vein = max(v1, v2);
    float grow = roots * (uRadius * 2.2);
    float reach = 1.0 - smoothstep(grow - 1.2, grow, d + n * 1.2);
    vec3 gold = mix(vec3(0.85, 0.6, 0.22), vec3(1.0, 0.86, 0.45), shaft);
    col = mix(earth, gold, vein * reach * (0.75 + 0.25 * sin(uT * 1.3 - d * 0.8)));
    col += gold * vein * reach * shaft * 0.35;
    local = xz;
  } else {
    col = vec3(0.92, 0.86, 0.74);
    local = vPos.xz;
  }
  col += vec3(1.0, 0.78, 0.45) * (shaft * 0.22 + dust);
  // the angan melts downward into the painting's browns and greens; the 4pm light stays
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5 + vec2(0.0, uPhase * 0.02));
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  float m = smoothstep(0.0, 0.25, melt * 1.3 - drip * 0.6 - n * 0.25);
  vec3 lit = paint + vec3(1.0, 0.78, 0.45) * shaft * 0.12;
  col = mix(col, lit, m);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
  figFrag: `
uniform sampler2D uFig;
uniform float uAppear, uThread, uFade, uDir, uT;
varying vec2 vUv;
void main(){
  vec4 c = texture2D(uFig, vUv);
  float melt = smoothstep(0.0, 1.0, uFade * 1.4 - (1.0 - vUv.y) * 0.4);
  float a = c.a * uAppear * (1.0 - melt);
  if (a < 0.01) discard;
  gl_FragColor = vec4(c.rgb, a);
  #include <colorspace_fragment>
}`,
};
